// Auto-Sequences toggle + multi-touch cadence table + smart-scheduling
// preview. Extracted from Settings.jsx (was ~500 lines mid-file).
import {
  Clock, Sparkles, RefreshCw, Loader2, Save,
  GripVertical, Plus, Trash2, RotateCcw,
} from "lucide-react";
import { Section, Field } from "./common";

export default function SequencesSection({
  form, set, save, saving,
  updateStep, removeStep, addStep, resetCadence, saveCadence,
  smartPreview, previewLoading, fetchSmartPreview,
}) {
  return (
    <>
      {/* Auto-Sequences */}
      <Section
        title="Auto-Sequences (Multi-Touch SDR)"
        subtitle="Once enabled, every new lead with a phone enters an automated cadence: AI personalized SMS → wait → AI call. The Vapi voice agent runs the call; you keep the timer."
        icon={<Clock className="w-4 h-4" />}
      >
        <label className="flex items-center gap-3 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.sequences_enabled}
            onChange={(e) => set("sequences_enabled", e.target.checked)}
            disabled={!form.tcpa_acknowledged}
            data-testid="toggle-sequences"
            className="w-4 h-4 accent-[#002FA7] disabled:opacity-50"
          />
          <div>
            <div className="font-semibold text-sm">Enable AI auto-sequences</div>
            <div className="text-xs text-zinc-500">
              New leads are queued automatically.
              {!form.tcpa_acknowledged && (
                <span className="text-amber-700"> Acknowledge TCPA below first.</span>
              )}
            </div>
          </div>
        </label>

        {/* TCPA acknowledgement — must be checked to enable sequences in the US. */}
        <label className="flex items-start gap-3 mb-5 cursor-pointer border border-amber-200 bg-amber-50 p-3" data-testid="tcpa-block">
          <input
            type="checkbox"
            checked={!!form.tcpa_acknowledged}
            onChange={(e) => set("tcpa_acknowledged", e.target.checked)}
            data-testid="toggle-tcpa"
            className="w-4 h-4 mt-0.5 accent-amber-700"
          />
          <div className="text-xs leading-relaxed text-amber-900">
            <strong>TCPA / compliance acknowledgement.</strong> I confirm I have a legitimate
            business relationship or prior consent to contact the phone numbers in my
            campaigns and will respect opt-outs (STOP keyword). The system enforces a hard
            <span className="font-mono mx-1 bg-white border border-amber-300 px-1.5">8:00 AM – 9:00 PM</span>
            local quiet-hour window regardless of campaign settings.
          </div>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="First SMS after (minutes)" testid="delay-sms">
            <input
              type="number" min={0} max={1440}
              value={form.delay_first_sms_minutes}
              onChange={(e) => set("delay_first_sms_minutes", Number(e.target.value))}
              data-testid="input-delay-sms"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
            />
          </Field>
          <Field label="Follow-up call after (hours)" testid="delay-call">
            <input
              type="number" min={1} max={168}
              value={form.delay_followup_call_hours}
              onChange={(e) => set("delay_followup_call_hours", Number(e.target.value))}
              data-testid="input-delay-call"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
            />
          </Field>
          <Field label="Mark lost after (hours)" testid="delay-lost">
            <input
              type="number" min={24} max={720}
              value={form.delay_lost_after_hours}
              onChange={(e) => set("delay_lost_after_hours", Number(e.target.value))}
              data-testid="input-delay-lost"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
            />
          </Field>
        </div>

        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="btn-save-sequence"
          className="mt-5 bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save Cadence
        </button>
      </Section>

      <Section
        title="Multi-Touch Cadence (7-step default)"
        subtitle="Each row is a touchpoint. Hour/minute are local to the lead's state timezone (TX → CT, FL → ET, etc). Touches landing outside business hours slide forward to the next valid slot. The 'angle' is fed to the AI so each touch reads differently."
        icon={<Clock className="w-4 h-4" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5" data-testid="business-hours-grid">
          <Field label="Business hours start (local)">
            <input
              type="number" min={0} max={23}
              value={form.business_hours_start}
              onChange={(e) => set("business_hours_start", Number(e.target.value))}
              data-testid="input-bh-start"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
            />
          </Field>
          <Field label="Business hours end (local)">
            <input
              type="number" min={1} max={24}
              value={form.business_hours_end}
              onChange={(e) => set("business_hours_end", Number(e.target.value))}
              data-testid="input-bh-end"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
            />
          </Field>
          <Field label="Weekdays only?">
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.business_days_only}
                onChange={(e) => set("business_days_only", e.target.checked)}
                data-testid="input-weekdays-only"
                className="w-4 h-4 accent-[#002FA7]"
              />
              <span className="text-sm">Skip Saturdays + Sundays</span>
            </label>
          </Field>
        </div>

        {/* Smart Scheduling toggle */}
        <div className="border border-dashed border-blue-300 bg-blue-50/30 p-4 mb-5" data-testid="smart-scheduling-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.smart_scheduling}
              onChange={(e) => set("smart_scheduling", e.target.checked)}
              data-testid="input-smart-scheduling"
              className="w-4 h-4 accent-[#002FA7] mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#002FA7]" />
                <span className="text-sm font-bold uppercase tracking-wider">Smart scheduling (learns your best hours)</span>
              </div>
              <p className="text-xs text-zinc-600 mt-1.5">
                When ON, each touchpoint's hour is nudged toward the hour-of-day where YOUR prospects have historically replied most for that weekday. Drift capped at ±3 hours from the configured time, clamped to business hours. Needs ≥3 replies in the last 60 days per weekday to activate.
              </p>
              <button
                type="button"
                onClick={fetchSmartPreview}
                disabled={previewLoading}
                data-testid="btn-smart-preview"
                className="mt-3 text-xs font-bold uppercase tracking-widest text-[#002FA7] hover:underline flex items-center gap-1.5 disabled:opacity-50"
              >
                {previewLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Preview what it learned from your inbox
              </button>
              {smartPreview && (
                <div className="mt-3 grid grid-cols-7 gap-1.5" data-testid="smart-preview-grid">
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((label, idx) => {
                    const hr = smartPreview.best_hours?.[idx];
                    return (
                      <div
                        key={idx}
                        className={`text-center py-2 border ${
                          hr != null
                            ? "border-[#002FA7] bg-white"
                            : "border-zinc-200 bg-zinc-50"
                        }`}
                        data-testid={`smart-preview-day-${idx}`}
                      >
                        <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
                        <div className={`text-sm font-mono font-bold mt-1 ${hr != null ? "text-[#002FA7]" : "text-zinc-300"}`}>
                          {hr != null ? `${String(hr).padStart(2,"0")}:00` : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {smartPreview && !smartPreview.has_enough_signal && (
                <p className="text-[10px] text-zinc-500 font-mono mt-2">
                  Not enough signal yet. Smart scheduling will activate per-weekday as replies pile up.
                </p>
              )}
            </div>
          </label>
        </div>

        <div className="border border-zinc-200" data-testid="cadence-table">
          <div className="grid grid-cols-[40px_1fr_1fr_1fr_1.5fr_40px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
            <div>#</div>
            <div>Day</div>
            <div>Time (local)</div>
            <div>Channel</div>
            <div>Angle (AI hint)</div>
            <div></div>
          </div>
          {form.sequence_steps.map((step, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[40px_1fr_1fr_1fr_1.5fr_40px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 items-center"
              data-testid={`cadence-row-${idx}`}
            >
              <div className="text-xs font-mono text-zinc-500 flex items-center gap-1">
                <GripVertical className="w-3 h-3" /> {idx + 1}
              </div>
              <input
                type="number" min={0} max={30}
                value={step.day_offset ?? 0}
                onChange={(e) => updateStep(idx, { day_offset: Number(e.target.value) })}
                data-testid={`cadence-day-${idx}`}
                className="px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7]"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number" min={0} max={23}
                  value={step.hour ?? 10}
                  onChange={(e) => updateStep(idx, { hour: Number(e.target.value) })}
                  data-testid={`cadence-hour-${idx}`}
                  className="w-12 px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7]"
                />
                <span className="text-xs text-zinc-400">:</span>
                <input
                  type="number" min={0} max={59}
                  value={step.minute ?? 0}
                  onChange={(e) => updateStep(idx, { minute: Number(e.target.value) })}
                  data-testid={`cadence-minute-${idx}`}
                  className="w-12 px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7]"
                />
              </div>
              <select
                value={step.kind || "sms"}
                onChange={(e) => updateStep(idx, { kind: e.target.value })}
                data-testid={`cadence-kind-${idx}`}
                className="px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7] bg-white"
              >
                <option value="sms">SMS</option>
                <option value="call">Call</option>
                <option value="email">Email</option>
                <option value="mark_lost">Mark Lost</option>
              </select>
              <select
                value={step.angle || ""}
                onChange={(e) => updateStep(idx, { angle: e.target.value })}
                data-testid={`cadence-angle-${idx}`}
                className="px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7] bg-white"
              >
                <option value="">— none —</option>
                <optgroup label="Built-in">
                  <option value="intro">intro (first touch)</option>
                  <option value="value">value (new angle / case study)</option>
                  <option value="first_attempt">first call attempt</option>
                  <option value="second_attempt">second call attempt</option>
                  <option value="breakup">breakup (soft goodbye)</option>
                  <option value="last_call">last call</option>
                </optgroup>
                {form.custom_angles && form.custom_angles.length > 0 && (
                  <optgroup label="Custom">
                    {form.custom_angles
                      .filter((a) => a.key)
                      .map((a) => (
                        <option key={a.key} value={a.key}>{a.label || a.key}</option>
                      ))}
                  </optgroup>
                )}
              </select>
              <button
                type="button"
                onClick={() => removeStep(idx)}
                data-testid={`cadence-remove-${idx}`}
                className="text-zinc-400 hover:text-red-600 flex items-center justify-center"
                title="Remove this touch"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {form.sequence_steps.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-zinc-500 font-mono">
              No steps configured — save to reset to the 7-step default.
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={addStep}
            data-testid="btn-cadence-add"
            className="px-3 py-2 border border-zinc-300 hover:bg-zinc-50 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add touchpoint
          </button>
          <button
            type="button"
            onClick={saveCadence}
            disabled={saving}
            data-testid="btn-cadence-save"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save cadence
          </button>
          <button
            type="button"
            onClick={resetCadence}
            disabled={saving}
            data-testid="btn-cadence-reset"
            className="text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-red-600 flex items-center gap-1.5"
          >
            <RotateCcw className="w-3 h-3" /> Reset to 7-step default
          </button>
        </div>

        <p className="mt-3 text-xs text-zinc-500">
          <strong>Tip:</strong> the SDR best-practice baseline is 7–10 touches over 14 days, alternating SMS and calls with varying hour-of-day. Don't fire two touches the same day to the same lead.
        </p>
      </Section>
    </>
  );
}
